// VideosPage.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import SubscriptionVideosGenre from '../../components/Videos/SubscriptionVideosGenre';
import VideoBanner from '../../components/Videos/VideoBanner';
import SubscriptionVideosListing from '../../components/Videos/SubscriptionVideoListing';
import { actions } from '../../util/actions';

const breakpoints = {
  tablet: 768,
  phone:  480,
};

const DashboardContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
  min-height: 75vh;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  box-sizing: border-box;

  @media (max-width: ${breakpoints.tablet}px) {
    padding: 16px;
  }

  @media (max-width: ${breakpoints.phone}px) {
    padding: 12px;
  }
`;

const ResourcesContainer = styled.div`
  display: grid;
  grid-template-columns: minmax(240px, 25%) minmax(0, 75%);
  gap: 20px;
  width: 100%;
  margin: 20px 0 0;
  align-items: flex-start;

  @media (max-width: ${breakpoints.tablet}px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  @media (max-width: ${breakpoints.phone}px) {
    gap: 12px;
  }
`;

const GenreContainer = styled.div`
  width: 100%;

  @media (max-width: ${breakpoints.phone}px) {
    display: none;
  }
`;

const BurgerButton = styled.button`
  display: none;
  @media (max-width: ${breakpoints.phone}px) {
    position: absolute;
    // top: ${({ more_top }) => (more_top ? '20%' : '9%')};
    margin-top:-200px;
    left: 10px;
    display: block;
    color: white;
    background: none;
    border: none;
    font-size: 2rem;
    margin-bottom: 12px;
    cursor: pointer;
  }
`;

const Overlay = styled.div`
  display: none;
  @media (max-width: ${breakpoints.phone}px) {
    display: ${({ open }) => (open ? 'block' : 'none')};
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0,0,0,0.4);
    z-index: 10;
  }
`;

const Sidebar = styled.div`
  display: none;
  @media (max-width: ${breakpoints.phone}px) {
    display: block;
    position: fixed;
    top: 0; left: 0;
    height: 100vh;
    width: 80%;
    max-width: 300px;
    background: white;
    box-shadow: 2px 0 8px rgba(0,0,0,0.2);
    transform: ${({ open }) =>
      open ? 'translateX(0)' : 'translateX(-100%)'};
    transition: transform 0.25s ease;
    z-index: 11;
    padding: 2rem 1rem;
    box-sizing: border-box;
  }
`;

const CloseButton = styled.button`
  display: none;
  @media (max-width: ${breakpoints.phone}px) {
    display: block;
    position: absolute;
    top: 12px; right: 12px;
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
  }
`;

const CurriculumContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: ${breakpoints.tablet}px) {
    width: 100%;
  }

  @media (max-width: ${breakpoints.phone}px) {
    gap: 12px;
  }
`;

const VideoBannerWrapper = styled.div`
  width: 100%;
`;

const ListingWrapper = styled.div`
  width: 100%;
`;

export default function VideosPage({ colors }) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const subscriptionVideos = useSelector((state) => state.auth.subscription_videos);
   const ISAFE_ROLES = new Set([
    "isafe_accountant",
    "isafe_moderator",
    "isafe_salesperson",
    "isafe_admin",
    "root",
  ]);
  const SUBSCRIPTION_ROLES = new Set([
    "subscription_access",
    "subscription_accountant",
    "subscription_admin",
    "subscription_implementor",
    "subscription_salesperson",
  ]);
    const become = useSelector(state =>state.auth.become);
    const become_user = useSelector(state =>state.auth.become_user);
    const roles = useSelector(state => state.auth.user.roles);
    const roleIDs = new Set(roles?.map((r) => r.id));
    const hasSubscriptionAdminRole = Array.from(SUBSCRIPTION_ROLES).some((r) =>
          roleIDs.has(r)
        );
    const hasISAFEAdminRole = Array.from(ISAFE_ROLES).some((r) => roleIDs.has(r));
  
    const more_top = (become == 1 && hasISAFEAdminRole ) || ((become == 0 && hasSubscriptionAdminRole && !hasISAFEAdminRole ))

  const [selectedGenre, setSelectedGenre] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    dispatch({ type: actions.FETCH_RESOURCE_DOWNLOAD_ASYNC });
  }, [dispatch]);

  const availableGenres = useMemo(() => {
    if (
      subscriptionVideos &&
      typeof subscriptionVideos === 'object' &&
      !Array.isArray(subscriptionVideos)
    ) {
      return Object.keys(subscriptionVideos).sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: 'base' })
      );
    }
    return [];
  }, [subscriptionVideos]);

  useEffect(() => {
    if (!availableGenres.length) {
      if (selectedGenre) {
        setSelectedGenre('');
      }
      return;
    }

    if (!selectedGenre || !availableGenres.includes(selectedGenre)) {
      setSelectedGenre(availableGenres[0]);
    }
  }, [availableGenres, selectedGenre]);

  const handleGenreSelection = (genre) => {
    setSelectedGenre(genre);
    setMenuOpen(false);
  };

  return (
    <DashboardContainer>
      {/* burger icon on phone */}
       {menuOpen == false &&
      <BurgerButton more_top={more_top} onClick={() => setMenuOpen(true)}>☰</BurgerButton>
       }
      {/* overlay + sidebar on phone */}
      <Overlay open={menuOpen} onClick={() => setMenuOpen(false)} />
      <Sidebar open={menuOpen}>
        <CloseButton onClick={() => setMenuOpen(false)}>×</CloseButton>
        <SubscriptionVideosGenre
          colors={colors}
          onGenreSelect={handleGenreSelection}
          selectedGenre={selectedGenre}
        />
      </Sidebar>

      <ResourcesContainer>
        {/* inline genre list on tablet+ */}
        <GenreContainer>
        <SubscriptionVideosGenre
          colors={colors}
          onGenreSelect={handleGenreSelection}
          selectedGenre={selectedGenre}
        />
        </GenreContainer>

        {/* full-width banner + listing */}
        <CurriculumContainer>
          <VideoBannerWrapper>
            <VideoBanner />
          </VideoBannerWrapper>
          <ListingWrapper>
            <SubscriptionVideosListing
              genre={selectedGenre}
              colors={colors}
            />
          </ListingWrapper>
        </CurriculumContainer>
      </ResourcesContainer>
    </DashboardContainer>
  );
}
